import { db } from '../config/firebase';
import {
  collection, getDocs, getDoc, doc, setDoc, addDoc, updateDoc,
  deleteDoc, query, where, serverTimestamp, arrayUnion,
} from 'firebase/firestore';

// ── Vitambulisho vya collections mpya ──────────────────────────────────
export const C = {
  members: 'members',
  clubs: 'clubs',
  reports: 'programReports',
  announcements: 'announcements',
};

// ══ KIZUIZI CHA RIPOTI KURUDIWA ═══════════════════════════════════════
// ID ya ripoti binafsi inatengenezwa kutokana na programu + mtu + wiki.
// Hivyo HAIWEZEKANI kabisa kuwa na ripoti mbili za mtu mmoja kwa wiki
// moja — hata staff wawili wakijaribu kwa wakati mmoja.
export function individualReportId(programId, memberId, weekKey) {
  return `${programId}__${memberId}__${weekKey}`;
}

export function staffWeeklyId(uid, weekKey) {
  return `staffweek__${uid}__${weekKey}`;
}

// ── Jina la staff (kutoka users/{uid}, kisha displayName, kisha email) ──
export async function resolveAuthorName(currentUser) {
  if (!currentUser) return 'Staff';
  try {
    const snap = await getDoc(doc(db, 'users', currentUser.uid));
    if (snap.exists()) {
      const u = snap.data();
      const nm = `${u.firstName || ''} ${u.lastName || ''}`.trim();
      if (nm) return nm;
      if (u.name) return u.name;
    }
  } catch (err) { /* rules zinaweza kuzuia — tunaendelea */ }
  return currentUser.displayName || currentUser.email || 'Staff';
}

// ── Wanachama ──────────────────────────────────────────────────────────
export async function fetchMembers(programId) {
  const q = programId
    ? query(collection(db, C.members), where('programIds', 'array-contains', programId))
    : collection(db, C.members);
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(m => m.active !== false)
    .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
}

export async function fetchMember(id) {
  const snap = await getDoc(doc(db, C.members, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function saveMember(data, id) {
  if (id) {
    await updateDoc(doc(db, C.members, id), { ...data, updatedAt: serverTimestamp() });
    return id;
  }
  const ref = await addDoc(collection(db, C.members), {
    ...data, active: true, createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function archiveMember(id) {
  await updateDoc(doc(db, C.members, id), { active: false, archivedAt: serverTimestamp() });
}

// ── Vilabu ─────────────────────────────────────────────────────────────
export async function fetchClubs(programId) {
  const q = programId
    ? query(collection(db, C.clubs), where('programId', '==', programId))
    : collection(db, C.clubs);
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

export async function saveClub(data, id) {
  if (id) {
    await updateDoc(doc(db, C.clubs, id), { ...data, updatedAt: serverTimestamp() });
    return id;
  }
  const ref = await addDoc(collection(db, C.clubs), { ...data, createdAt: serverTimestamp() });
  return ref.id;
}

export async function deleteClub(id) {
  await deleteDoc(doc(db, C.clubs, id));
}

// ── Ripoti ─────────────────────────────────────────────────────────────
// Kuchuja kunafanyika UPANDE WA SERVER pale inapowezekana. Awali tulikuwa
// tunapakua collection nzima kila mara — ingefanya kazi mwaka wa kwanza,
// lakini ripoti 15,000 zikifika, kila ukurasa ungekuwa unapakua zote.
// Firestore inaruhusu vichujio kadhaa vya usawa bila index ya composite.
export async function fetchReports({ programId, kind, weekKey, memberId, authorId } = {}) {
  const clauses = [];
  if (programId) clauses.push(where('programId', '==', programId));
  if (kind) clauses.push(where('kind', '==', kind));
  if (weekKey) clauses.push(where('weekKey', '==', weekKey));
  if (memberId) clauses.push(where('memberId', '==', memberId));
  if (authorId) clauses.push(where('authorId', '==', authorId));

  let list;
  try {
    const qy = clauses.length
      ? query(collection(db, C.reports), ...clauses)
      : collection(db, C.reports);
    const snap = await getDocs(qy);
    list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    // Index ikikosekana au kichujio kisikubalike, tunarudi kwenye njia ya
    // zamani ili ukurasa usikwame kabisa.
    console.warn('Kuchuja kwa server kumeshindikana, tunachuja hapa:', err);
    const snap = await getDocs(collection(db, C.reports));
    list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .filter(r => (!programId || r.programId === programId)
        && (!kind || r.kind === kind)
        && (!weekKey || r.weekKey === weekKey)
        && (!memberId || r.memberId === memberId)
        && (!authorId || r.authorId === authorId));
  }

  return list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
}

export async function fetchReportById(id) {
  const snap = await getDoc(doc(db, C.reports, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// Ripoti binafsi — setDoc kwa ID maalum (ONE per member per week)
export async function saveIndividualReport({ programId, member, weekKey, weekLabel, values, author, clubId, clubName }) {
  const id = individualReportId(programId, member.id, weekKey);
  const existing = await fetchReportById(id);

  const payload = {
    kind: 'individual',
    programId,
    memberId: member.id,
    memberName: member.fullName || '',
    memberType: member.type || '',
    clubId: clubId || member.clubId || '',
    clubName: clubName || member.clubName || '',
    weekKey,
    weekLabel,
    values,
    updatedAt: serverTimestamp(),
    lastEditedBy: author.uid,
    lastEditedByName: author.name,
  };

  if (existing) {
    await updateDoc(doc(db, C.reports, id), payload);
  } else {
    await setDoc(doc(db, C.reports, id), {
      ...payload,
      authorId: author.uid,
      authorName: author.name,
      contributions: [],
      createdAt: serverTimestamp(),
    });
  }
  return id;
}

// Nyongeza — staff mwingine anaongeza maoni badala ya kuandika ripoti mpya
export async function addContribution(reportId, author, text) {
  await updateDoc(doc(db, C.reports, reportId), {
    contributions: arrayUnion({
      by: author.uid,
      byName: author.name,
      text,
      at: new Date().toISOString(),
    }),
    updatedAt: serverTimestamp(),
  });
}

export async function saveSessionReport(data, author, id) {
  if (id) {
    await updateDoc(doc(db, C.reports, id), {
      ...data, updatedAt: serverTimestamp(),
      lastEditedBy: author.uid, lastEditedByName: author.name,
    });
    return id;
  }
  const ref = await addDoc(collection(db, C.reports), {
    ...data,
    kind: 'session',
    authorId: author.uid,
    authorName: author.name,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function saveStaffWeekly({ weekKey, weekLabel, values, programIds, author }) {
  const id = staffWeeklyId(author.uid, weekKey);
  const existing = await fetchReportById(id);
  const payload = {
    kind: 'staffWeekly',
    programId: 'all',
    programIds: programIds || [],
    weekKey, weekLabel, values,
    updatedAt: serverTimestamp(),
  };
  if (existing) {
    await updateDoc(doc(db, C.reports, id), payload);
  } else {
    await setDoc(doc(db, C.reports, id), {
      ...payload,
      authorId: author.uid,
      authorName: author.name,
      createdAt: serverTimestamp(),
    });
  }
  return id;
}

export async function deleteReport(id) {
  await deleteDoc(doc(db, C.reports, id));
}

// ── Matangazo ──────────────────────────────────────────────────────────
export async function fetchAnnouncements() {
  const snap = await getDocs(collection(db, C.announcements));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
}

export async function saveAnnouncement(data, id) {
  if (id) {
    await updateDoc(doc(db, C.announcements, id), { ...data, updatedAt: serverTimestamp() });
    return id;
  }
  const ref = await addDoc(collection(db, C.announcements), { ...data, createdAt: serverTimestamp() });
  return ref.id;
}

export async function deleteAnnouncement(id) {
  await deleteDoc(doc(db, C.announcements, id));
}

// ── Majina ya programu (yanayohifadhiwa Firestore) ─────────────────────
export async function fetchProgramOverrides() {
  const snap = await getDocs(collection(db, 'programs'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function saveProgramOverride(id, data) {
  await setDoc(doc(db, 'programs', id), {
    ...data, updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function resetProgramOverride(id) {
  await deleteDoc(doc(db, 'programs', id));
}

// ── Kuwaunganisha wanachama waliopo na programu nyingine ───────────────
// Hairudii taarifa zao — inaongeza tu programu kwenye rekodi ileile.
// Salama kurudia: aliye tayari ndani haongezwi mara mbili.
export async function linkMembersToProgram({ fromProgramId, toProgramId, types, clubId, clubName }) {
  const members = await fetchMembers(fromProgramId);
  const target = members.filter(m =>
    (!types || types.includes(m.type)) && !(m.programIds || []).includes(toProgramId)
  );

  let done = 0;
  for (const m of target) {
    const patch = {
      programIds: [...(m.programIds || []), toProgramId],
      updatedAt: serverTimestamp(),
    };
    // Wapangie club ya chess kama hawana
    if (clubId && !m.clubId) {
      patch.clubId = clubId;
      patch.clubName = clubName || '';
    }
    await updateDoc(doc(db, C.members, m.id), patch);
    done++;
  }

  return { scanned: members.length, linked: done, alreadyIn: members.length - target.length };
}
